import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Spinner } from 'react-bootstrap';
import { BsArrowClockwise, BsTelephoneInbound } from 'react-icons/bs';
import dayjs from 'dayjs';
import OurAccordion from '@/_elements/OurAccordion';
import Pagination from '@/components/Admin/Pagination';
import { ContainerStyles } from '@/components/Admin/Settings';
import { callCenterVoicemailsApi } from '@/_services/api/callCenter/callCenterVoicemails';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';

const formatDuration = (seconds) => {
    const value = Number(seconds) || 0;
    return `${Math.floor(value / 60)}:${String(value % 60).padStart(2, '0')}`;
};

const VoicemailItem = ({ voicemail }) => {
    const caller = voicemail.callerE164 || 'Unknown caller';
    const calledNumber = voicemail.calledE164 || 'Unknown number';
    const latestError = voicemail.errorStack?.[voicemail.errorStack.length - 1]?.message;
    const receivedAt = voicemail.createdAt
        ? dayjs(voicemail.createdAt).format('MMM D, YYYY · h:mm A')
        : 'Date unavailable';

    return (
        <div className="mb-3">
            <OurAccordion
                defaultShow={false}
                titleTag={null}
                title={
                    <div className="d-flex align-items-center justify-content-between w-100 pr-2">
                        <div className="d-flex align-items-center min-width-0">
                            <BsTelephoneInbound aria-hidden="true" size={18} className="flex-shrink-0 mr-3" />
                            <div className="min-width-0">
                                <div className="font-weight-bold text-truncate">{caller}</div>
                                <small className="text-muted">{receivedAt}</small>
                            </div>
                        </div>
                        <div className="d-flex align-items-center ml-3 flex-shrink-0">
                            <Badge variant={voicemail.status === 'ready' ? 'success' : 'secondary'}>
                                {voicemail.status === 'ready' ? 'Ready' : voicemail.status}
                            </Badge>
                            <small className="text-muted ml-2">{formatDuration(voicemail.durationSeconds)}</small>
                        </div>
                    </div>
                }
            >
                <div className="d-flex flex-wrap align-items-start mb-3">
                    <div className="mr-5 mb-2">
                        <small className="d-block text-muted">Caller</small>
                        <span className="font-weight-bold">{caller}</span>
                    </div>
                    <div className="mb-2">
                        <small className="d-block text-muted">Called number</small>
                        <span className="font-weight-bold">{calledNumber}</span>
                    </div>
                </div>
                {voicemail.audioUrl ? (
                    <audio controls preload="none" className="w-100" aria-label={`Voicemail from ${caller}`}>
                        <source src={voicemail.audioUrl} />
                        Your browser does not support audio playback.
                    </audio>
                ) : (
                    <p className="text-muted mb-2">
                        {latestError || 'The recording is not available yet.'}
                    </p>
                )}
            </OurAccordion>
        </div>
    );
};

const VoicemailsTabComponent = ({ teamId }) => {
    const [voicemails, setVoicemails] = useState([]);
    const [totalVoicemails, setTotalVoicemails] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const loadVoicemails = useCallback(async () => {
        if (!teamId) {
            setVoicemails([]);
            setTotalVoicemails(0);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError('');
        try {
            const response = await callCenterVoicemailsApi.getVoicemails({ team: teamId, limit: 20, page });
            setVoicemails(response?.results || []);
            setTotalVoicemails(response?.totalResults || 0);
        } catch (requestError) {
            setError(requestError?.message || 'Could not load this team’s voicemails.');
        } finally {
            setLoading(false);
        }
    }, [teamId, page]);

    useEffect(() => {
        loadVoicemails();
    }, [loadVoicemails]);

    return (
        <section aria-label="Team voicemails">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div></div>
                <Button variant="outline-secondary" onClick={loadVoicemails} disabled={loading}>
                    {loading ? (
                        <Spinner size="sm" animation="border" aria-hidden="true" />
                    ) : (
                        <BsArrowClockwise aria-hidden="true" />
                    )}
                    <span className="ml-2">Refresh</span>
                </Button>
            </div>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                    {error}
                </Alert>
            )}
            {loading ? (
                <div className="text-center py-5" role="status">
                    <Spinner animation="border" />
                    <p className="text-muted mt-2 mb-0">Loading voicemails…</p>
                </div>
            ) : voicemails.length === 0 ? (
                <div className="text-center py-5 border rounded">
                    <BsTelephoneInbound size={28} className="text-muted mb-2" aria-hidden="true" />
                    <p className="font-weight-bold mb-1">No voicemails yet</p>
                    <p className="text-muted mb-0">New recordings will appear here after callers leave a message.</p>
                </div>
            ) : (
                voicemails.map((voicemail) => (
                    <VoicemailItem key={voicemail.id || voicemail._id} voicemail={voicemail} />
                ))
            )}
            {!loading && totalVoicemails > 0 && (
                <ContainerStyles className="py-3 d-flex justify-content-center">
                    <Pagination
                        totalRecords={totalVoicemails}
                        pageLimit={20}
                        pageNeighbours={1}
                        pageNo={page}
                        onPageChanged={({ currentPage }) => setPage(Math.max(1, currentPage || 1))}
                    />
                </ContainerStyles>
            )}
        </section>
    );
};

const VoicemailsTab = (props) => (
    <ComponentErrorBoundary componentErrorMessage={props?.componentErrorMessage}>
        <VoicemailsTabComponent {...props} />
    </ComponentErrorBoundary>
);

export default VoicemailsTab;
